import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, ShoppingCart, FileText, CreditCard, AlertTriangle, DollarSign,
  CheckCircle, Clock, Eye, Banknote, Calendar, Search, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PurchasesPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    compras_mes: 0, cxp_pendientes: 0, cxp_vencidas: 0,
    compras_pagadas_hoy: 0, proximos_vencimientos_7_dias: 0,
    total_compras_mes: 0, total_pendientes: 0, total_vencidas: 0
  });
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [constants, setConstants] = useState({ payment_conditions: [], payment_statuses: [], payment_methods: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('todas');

  // Filters
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  // Payment Form
  const [paymentData, setPaymentData] = useState({
    monto: '', metodo_pago: 'transferencia', fecha_pago: '', referencia: '', notas: ''
  });

  // Fetch functions
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/purchases/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/purchases/?`;
      
      // Map tab to estado_pago
      if (activeTab === 'pendientes') url += `estado_pago=pendiente&`;
      else if (activeTab === 'vencidas') url += `estado_pago=vencida&`;
      else if (activeTab === 'pagadas') url += `estado_pago=pagada&`;
      
      if (filterSupplier && filterSupplier !== 'todos') url += `proveedor_id=${filterSupplier}&`;
      if (filterCondition && filterCondition !== 'todas') url += `condicion_pago=${filterCondition}&`;
      if (dateFrom) url += `fecha_desde=${dateFrom}&`;
      if (dateTo) url += `fecha_hasta=${dateTo}&`;
      if (searchQuery) url += `search=${searchQuery}&`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, filterSupplier, filterCondition, dateFrom, dateTo, searchQuery]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suppliers/search/quick?q=&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  }, [token]);

  const fetchConstants = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/purchases/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSuppliers();
    fetchConstants();
  }, [fetchStats, fetchSuppliers, fetchConstants]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  // Open payment modal
  const openPaymentModal = (purchase) => {
    setSelectedPurchase(purchase);
    setPaymentData({
      monto: purchase.total,
      metodo_pago: 'transferencia',
      fecha_pago: new Date().toISOString().split('T')[0],
      referencia: '',
      notas: ''
    });
    setShowPaymentModal(true);
  };

  // Process payment
  const handlePayment = async () => {
    if (!paymentData.monto || parseFloat(paymentData.monto) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    try {
      await axios.post(`${API}/purchases/pagar`, {
        compra_id: selectedPurchase.id,
        monto: parseFloat(paymentData.monto),
        metodo_pago: paymentData.metodo_pago,
        fecha_pago: paymentData.fecha_pago || null,
        referencia: paymentData.referencia || null,
        notas: paymentData.notas || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pago registrado exitosamente');
      setShowPaymentModal(false);
      fetchPurchases();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const formatCurrency = (value) => `Q ${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const getStatusBadge = (purchase) => {
    if (purchase.estado_pago === 'pagada') {
      return <Badge className="bg-green-100 text-green-700 border-green-300">Pagada</Badge>;
    }
    if (purchase.estado_pago === 'vencida') {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-300">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Vencida ({purchase.dias_vencido}d)
        </Badge>
      );
    }
    // Check if próxima a vencer (7 días)
    if (purchase.fecha_vencimiento_pago) {
      const today = new Date();
      const venc = new Date(purchase.fecha_vencimiento_pago);
      const diff = Math.ceil((venc - today) / (1000 * 60 * 60 * 24));
      if (diff <= 7 && diff >= 0) {
        return <Badge className="bg-amber-100 text-amber-700 border-amber-300">Vence en {diff}d</Badge>;
      }
    }
    return <Badge className="bg-slate-100 text-slate-700 border-slate-300">Pendiente</Badge>;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-slate-900">Compras y Cuentas por Pagar</h1>
          <p className="text-slate-500 text-sm">Gestión de compras, facturas y pagos a proveedores</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/compras/agenda')}>
            <Calendar className="w-4 h-4 mr-2" />
            Agenda de Pagos
          </Button>
          <Button onClick={() => navigate('/admin/compras/nueva')} className="bg-[#059669] hover:bg-[#047857] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Compra
          </Button>
        </div>
      </div>

      {/* KPI Cards - 5 indicadores */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-display font-medium text-slate-900">{formatCurrency(stats.compras_mes)}</div>
            <div className="text-xs text-slate-500">{stats.total_compras_mes} compras del mes</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-display font-medium text-amber-700">{formatCurrency(stats.cxp_pendientes)}</div>
            <div className="text-xs text-amber-600">{stats.total_pendientes} CxP pendientes</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-display font-medium text-red-700">{formatCurrency(stats.cxp_vencidas)}</div>
            <div className="text-xs text-red-600">{stats.total_vencidas} CxP vencidas</div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-display font-medium text-green-700">{formatCurrency(stats.compras_pagadas_hoy)}</div>
            <div className="text-xs text-green-600">Pagado hoy</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4 pb-4">
            <div className="text-2xl font-display font-medium text-blue-700">{formatCurrency(stats.proximos_vencimientos_7_dias)}</div>
            <div className="text-xs text-blue-600">Vence próx. 7 días</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="todas">Todas</TabsTrigger>
            <TabsTrigger value="pendientes">Pendientes</TabsTrigger>
            <TabsTrigger value="vencidas" className="text-red-600">
              Vencidas {stats.total_vencidas > 0 && `(${stats.total_vencidas})`}
            </TabsTrigger>
            <TabsTrigger value="pagadas">Pagadas</TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar número compra o factura..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-4 items-center flex-wrap">
          <Select value={filterSupplier || "todos"} onValueChange={setFilterSupplier}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Proveedor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los proveedores</SelectItem>
              {suppliers.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nombre_comercial}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterCondition || "todas"} onValueChange={setFilterCondition}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Condición" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              {constants.payment_conditions.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-500">Desde:</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-500">Hasta:</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36"
            />
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">No. Compra</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Proveedor</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Fecha / Factura</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Total</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Condición</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Vencimiento</th>
                  <th className="text-center p-4 text-sm font-medium text-slate-600">Estado</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">Cargando...</td>
                  </tr>
                ) : purchases.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-500">No hay compras</td>
                  </tr>
                ) : (
                  purchases.map((purchase) => (
                    <tr 
                      key={purchase.id} 
                      className={`border-b hover:bg-slate-50 ${
                        purchase.estado_pago === 'vencida' ? 'bg-red-50' : ''
                      }`}
                    >
                      <td className="p-4">
                        <span className="font-mono font-medium text-slate-900">{purchase.numero_compra}</span>
                        {purchase.es_borrador && (
                          <Badge variant="outline" className="ml-2 text-xs">Borrador</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="font-medium text-slate-900">{purchase.proveedor_nombre}</div>
                        {purchase.proveedor_nit && (
                          <div className="text-xs text-slate-500">NIT: {purchase.proveedor_nit}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-slate-700">{purchase.fecha_compra}</div>
                        {purchase.numero_factura && (
                          <div className="text-xs text-slate-500">Fact: {purchase.numero_factura}</div>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <span className="font-medium text-slate-900">{formatCurrency(purchase.total)}</span>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="text-slate-600">
                          {purchase.condicion_pago_label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {purchase.fecha_vencimiento_pago ? (
                          <span className={purchase.estado_pago === 'vencida' ? 'text-red-700 font-medium' : 'text-slate-700'}>
                            {purchase.fecha_vencimiento_pago}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {getStatusBadge(purchase)}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          <Link to={`/admin/compras/${purchase.id}`}>
                            <Button size="sm" variant="outline" title="Ver detalle">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          {purchase.estado_pago !== 'pagada' && !purchase.es_borrador && (
                            <Button
                              size="sm"
                              onClick={() => openPaymentModal(purchase)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              title="Registrar pago"
                            >
                              <Banknote className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </Tabs>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Registrar Pago
            </DialogTitle>
          </DialogHeader>
          {selectedPurchase && (
            <div className="space-y-4">
              {/* Purchase Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Compra:</span>
                  <span className="font-mono font-medium">{selectedPurchase.numero_compra}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Proveedor:</span>
                  <span>{selectedPurchase.proveedor_nombre}</span>
                </div>
                {selectedPurchase.numero_factura && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Factura:</span>
                    <span>{selectedPurchase.numero_factura}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg pt-2 border-t">
                  <span className="font-medium">Total a pagar:</span>
                  <span className="font-bold text-green-700">{formatCurrency(selectedPurchase.total)}</span>
                </div>
              </div>

              {/* Payment Form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monto (Q) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paymentData.monto}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, monto: e.target.value }))}
                    disabled
                    className="bg-slate-100"
                  />
                  <p className="text-xs text-slate-500 mt-1">Pago único por el total</p>
                </div>
                <div>
                  <Label>Método de pago</Label>
                  <Select 
                    value={paymentData.metodo_pago} 
                    onValueChange={(v) => setPaymentData(prev => ({ ...prev, metodo_pago: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {constants.payment_methods.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha de pago</Label>
                  <Input
                    type="date"
                    value={paymentData.fecha_pago}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, fecha_pago: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Referencia</Label>
                  <Input
                    value={paymentData.referencia}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, referencia: e.target.value }))}
                    placeholder="No. cheque/transfer."
                  />
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea
                  value={paymentData.notas}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notas: e.target.value }))}
                  placeholder="Observaciones del pago..."
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancelar</Button>
            <Button onClick={handlePayment} className="bg-green-600 hover:bg-green-700 text-white">
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasesPage;
